// src/pages/reports/SuppliersReport.jsx
import { useMemo } from 'react'
import { ChevronDown, X } from 'lucide-react'
import BaseReportPage from '../../components/reports/BaseReportPage'

const SuppliersFilters = ({ filters, onFiltersChange, onClear }) => {

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const handleClearFilters = () => {
    onFiltersChange({
      supplier: '',
      branch: '',
      paymentType: ''
    })
    onClear()
  }

  const hasActiveFilters = useMemo(() => {
    return filters?.supplier || filters?.branch || filters?.paymentType
  }, [filters])

  // Payment type options
  const paymentTypeOptions = [
    { value: '', label: 'All Payment Types' },
    { value: 'paid', label: '✅ Paid' },
    { value: 'partially_paid', label: '⚠️ Partially Paid' },
    { value: 'unpaid', label: '❌ Unpaid' },
    { value: 'no_transactions', label: '📋 No Transactions' }
  ]

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Supplier Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search supplier..."
              value={filters?.supplier || ''}
              onChange={(e) => handleFilterChange('supplier', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Status</label>
          <div className="relative">
            <select
              value={filters?.activeOnly ? 'active' : ''}
              onChange={(e) => handleFilterChange('activeOnly', e.target.value === 'active')}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10 appearance-none"
            >
              <option value="">All Suppliers</option>
              <option value="active">🟢 Active Only</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Payment Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
          <div className="relative">
            <select
              value={filters?.paymentType || ''}
              onChange={(e) => handleFilterChange('paymentType', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10 appearance-none"
            >
              {paymentTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  )
}

const SuppliersReport = () => {
  const columns = useMemo(() => [
    { key: 'supplierId', label: 'Supplier ID', sortable: true },
    { key: 'supplierName', label: 'Supplier Name', sortable: true },
    { 
      key: 'contactPerson', 
      label: 'Contact Person', 
      sortable: true,
      render: (value) => value || '-'
    },
    { key: 'totalPurchase', label: 'Total Purchase', sortable: true, type: 'currency' },
    { key: 'paidAmount', label: 'Paid Amount', sortable: true, type: 'currency' },
    { key: 'dueAmount', label: 'Due Amount', sortable: true, type: 'currency' },
    { 
      key: 'status', 
      label: 'Payment Status', 
      sortable: true,
      render: (value) => {
        const statusStyles = {
          'Paid': 'bg-green-100 text-green-800',
          'Partially Paid': 'bg-yellow-100 text-yellow-800', 
          'Unpaid': 'bg-red-100 text-red-800',
          'No Transactions': 'bg-gray-100 text-gray-600'
        }
        const statusIcons = {
          'Paid': '✅',
          'Partially Paid': '⚠️',
          'Unpaid': '❌', 
          'No Transactions': '📋'
        }
        
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[value] || 'bg-gray-100 text-gray-600'}`}>
            <span className="mr-1">{statusIcons[value] || '❓'}</span>
            {value}
          </span>
        )
      }
    },
    { 
      key: 'grnCount', 
      label: 'GRN Count', 
      sortable: true,
      render: (value) => value || 0
    }
  ], [])

  return (
    <BaseReportPage
      title="Suppliers Report"
      subtitle="Complete supplier directory with GRN payment status and transaction history"
      reportType="suppliers"
      columns={columns}
      customFilterComponent={SuppliersFilters}
      enableFilters={true}
      fileName="suppliers-report"
    />
  )
}

export default SuppliersReport